import type { JSX } from "react";

import { MarkdownViewer } from "@expert/solve-tools";
import { ScrollArea, Skeleton, Stack, Text } from "@mantine/core";

import type { HighlightAssistReply as Reply } from "./askAssistAboutHighlight";

import { markdownOverrides } from "../timeline-list/markdownOverrides";
import classes from "./TextHighlightArea.module.css";

const replyMarkdownOptions = { overrides: markdownOverrides };

interface Props {
    reply: Reply;
}

/** Assist's answer to the highlight's "Tell me more" question: a skeleton until it arrives. */
export function TextHighlightAnswer({ reply }: Props): JSX.Element {
    if (reply.status === "loading") {
        return (
            <Stack gap="xs" py="3xs" data-testid="text-highlight-reply-loader" pr="sm">
                <Skeleton h={10} radius="xl" />
                <Skeleton h={10} w="60%" radius="xl" />
            </Stack>
        );
    }

    if (reply.status === "failed") {
        return (
            <Text size="sm" c="dark.1">
                {"Assist couldn't answer that right now. Try highlighting it again."}
            </Text>
        );
    }

    return (
        <ScrollArea.Autosize mah={240} scrollbars="y" type="auto" offsetScrollbars="y">
            {/* The answer often repeats the highlighted text, which can be the customer's words; mask it like the label. */}
            <div className={`${classes.reply} fs-mask`} data-testid="text-highlight-reply">
                <MarkdownViewer content={reply.text} options={replyMarkdownOptions} />
            </div>
        </ScrollArea.Autosize>
    );
}
